import { assertNever } from '@deltasierra/shared';
import { TooltipProps as MTooltipProps, makeStyles, Tooltip as MTooltip } from '@material-ui/core';
import { CreateCSSProperties, CSSProperties } from '@material-ui/core/styles/withStyles';
import * as React from 'react';
import { Theme } from '../../../theme';

export type TooltipColorType = 'black' | 'default' | 'info' | 'primary' | 'secondary';

function getColorFor(color: TooltipColorType, theme: Theme): string {
    switch (color) {
        case 'default':
        case 'black':
            return theme.palette.common.black;
        case 'primary':
            return theme.palette.primary.dark;
        case 'secondary':
            return theme.palette.secondary.dark;
        case 'info':
            return 'rgba(24, 37, 46, 0.9)';
        default:
            return assertNever(color);
    }
}

export type TooltipStyleType = 'default' | 'info';

function getTooltipStyleFor(
    style: TooltipStyleType,
    theme: Theme,
): CreateCSSProperties<TooltipStyleProps> | CSSProperties {
    switch (style) {
        case 'default':
            return {
                fontSize: theme.typography.body1.fontSize,
            };
        case 'info':
            return {
                borderRadius: '8px',
                fontSize: 13,
                fontWeight: 400,
                lineHeight: '21px',
                padding: '8px 12px',
                textAlign: 'center',
            };
        default:
            return assertNever(style);
    }
}

export type TooltipStyleProps = {
    color: TooltipColorType;
    style: TooltipStyleType;
    additionalTooltipStyles: CSSProperties;
};

const useTooltipStyles = makeStyles((theme: Theme) => ({
    arrow: (props: TooltipStyleProps) => ({
        color: getColorFor(props.color, theme),
    }),
    tooltip: (props: TooltipStyleProps) => ({
        backgroundColor: getColorFor(props.color, theme),
        ...getTooltipStyleFor(props.style, theme),
        ...props.additionalTooltipStyles,
    }),
}));

export type TooltipVariant = 'black' | 'info';

function getDefaultColorForVariant(variant: TooltipVariant | undefined): TooltipColorType {
    switch (variant) {
        case 'black':
            return 'black';
        case 'info':
            return 'info';
        case undefined:
            return 'default';
        default:
            return assertNever(variant);
    }
}

function getDefaultStyleForVariant(variant: TooltipVariant | undefined): TooltipStyleType {
    switch (variant) {
        case 'black':
            return 'default';
        case 'info':
            return 'info';
        case undefined:
            return 'default';
        default:
            return assertNever(variant);
    }
}

export type TooltipProps = Pick<MTooltipProps, 'children' | 'placement'> & {
    color?: TooltipStyleProps['color'];
    tip: React.ReactNode;
    style?: CSSProperties;
    tooltipStyle?: TooltipStyleProps['style'];
    variant?: TooltipVariant;
};

export function Tooltip({ color, style, tip, tooltipStyle, variant, ...props }: TooltipProps): JSX.Element {
    const classes = useTooltipStyles({
        additionalTooltipStyles: style ?? {},
        color: color ?? getDefaultColorForVariant(variant),
        style: tooltipStyle ?? getDefaultStyleForVariant(variant),
    });
    return (
        <MTooltip
            arrow
            classes={classes}
            /**
             * The tip will only be hidden if it equates to a zero length string, therefore take the falsy values and
             * return '' so we hide it, or the actual tip if it should be rendered
             */
            title={tip === false || tip === null || tip === undefined ? '' : tip}
            {...props}
        />
    );
}
Tooltip.displayName = 'Tooltip';
